import React, { useState } from "react";
import { useLanguage } from "../../context/LanguageContext";
import {
  Eye,
  FileText,
  Users,
  Globe,
  Building2,
  ShieldCheck,
  Languages,
} from "lucide-react";

const ChinaArbitrationCooperationPage = () => {
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(true);
  const [activeDocumentId, setActiveDocumentId] = useState("memorandum");

  const documentsData = [
    {
      id: "memorandum",
      titleKey: "china_coop_doc1_title",
      descriptionKey: "china_coop_doc1_desc",
      path: "/documents/合作备忘录.pdf",
      badgeKey: "china_coop_doc_badge_memo",
    },
    {
      id: "scan_2026",
      titleKey: "china_coop_doc2_title",
      descriptionKey: "china_coop_doc2_desc",
      path: "/documents/Сканирование 2 июля 2026 г..pdf",
      badgeKey: "china_coop_doc_badge_scan",
    },
  ];

  const activeDocument =
    documentsData.find((document) => document.id === activeDocumentId) ||
    documentsData[0];

  const handleDocumentSelect = (documentId) => {
    setActiveDocumentId(documentId);
    setIsLoading(true);
  };

  const benefits = [
    {
      icon: Users,
      title: t("china_coop_benefit1_title"),
      description: t("china_coop_benefit1_desc"),
      color: "text-blue-600",
      bg: "bg-blue-100",
    },
    {
      icon: Globe,
      title: t("china_coop_benefit2_title"),
      description: t("china_coop_benefit2_desc"),
      color: "text-green-600",
      bg: "bg-green-100",
    },
    {
      icon: Building2,
      title: t("china_coop_benefit3_title"),
      description: t("china_coop_benefit3_desc"),
      color: "text-orange-600",
      bg: "bg-orange-100",
    },
    {
      icon: ShieldCheck,
      title: t("china_coop_benefit4_title"),
      description: t("china_coop_benefit4_desc"),
      color: "text-purple-600",
      bg: "bg-purple-100",
    },
  ];

  return (
    <div className="pt-32 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg border border-blue-100 p-6 mb-8">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-blue-900 rounded-lg">
              <Languages className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-blue-900">
                {t("china_coop_title")}
              </h1>
              <p className="text-gray-600 mt-1">{t("china_coop_subtitle")}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {benefits.map((benefit, index) => {
            const IconComponent = benefit.icon;
            return (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-lg border border-blue-100 hover:shadow-xl transition-all text-center"
              >
                <div
                  className={`p-3 ${benefit.bg} rounded-lg w-16 h-16 mx-auto mb-4 flex items-center justify-center`}
                >
                  <IconComponent className={`w-8 h-8 ${benefit.color}`} />
                </div>
                <h3 className="text-lg font-semibold text-blue-900 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-lg shadow-lg border border-blue-100 overflow-hidden">
          <div className="bg-gray-100 px-6 py-3 border-b border-gray-200">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div className="flex items-center space-x-2">
                <Eye className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">
                  {t("document_view")}
                </span>
              </div>
              <span className="text-sm text-gray-500">
                {t("china_coop_documents_count")}
              </span>
            </div>
          </div>

          <div className="p-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              {documentsData.map((document) => {
                const isActive = document.id === activeDocument.id;

                return (
                  <button
                    key={document.id}
                    type="button"
                    onClick={() => handleDocumentSelect(document.id)}
                    className={`text-left p-5 rounded-xl border transition-all ${
                      isActive
                        ? "bg-blue-900 text-white border-blue-900 shadow-lg"
                        : "bg-white text-gray-700 border-blue-100 hover:border-blue-400 hover:shadow-md"
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={`p-3 rounded-lg ${
                          isActive ? "bg-white/15" : "bg-blue-50"
                        }`}
                      >
                        <FileText
                          className={`w-6 h-6 ${
                            isActive ? "text-white" : "text-blue-900"
                          }`}
                        />
                      </div>
                      <div>
                        <span
                          className={`inline-flex mb-2 px-3 py-1 rounded-full text-xs font-semibold ${
                            isActive
                              ? "bg-white/15 text-blue-50"
                              : "bg-blue-50 text-blue-800"
                          }`}
                        >
                          {t(document.badgeKey)}
                        </span>
                        <h3 className="text-lg font-semibold mb-2">
                          {t(document.titleKey)}
                        </h3>
                        <p
                          className={`text-sm ${
                            isActive ? "text-blue-100" : "text-gray-600"
                          }`}
                        >
                          {t(document.descriptionKey)}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto mb-4"></div>
                  <p className="text-gray-600">{t("document_loading")}</p>
                </div>
              </div>
            )}

            <div
              className="w-full border border-gray-200 rounded-lg overflow-hidden"
              style={{ height: "800px" }}
            >
              <iframe
                src={activeDocument.path}
                width="100%"
                height="100%"
                title={t(activeDocument.titleKey)}
                className="border-0"
                onLoad={() => setIsLoading(false)}
              />
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">
                {t("china_coop_info_title")}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <strong className="text-blue-800">{t("field_name")}:</strong>
                  <br />
                  {t(activeDocument.titleKey)}
                </div>
                <div>
                  <strong className="text-blue-800">{t("field_status")}:</strong>
                  <br />
                  {t("china_coop_info_status")}
                </div>
                <div>
                  <strong className="text-blue-800">{t("field_format")}:</strong>
                  <br />
                  {t("field_format_value")}
                </div>
                <div>
                  <strong className="text-blue-800">
                    {t("china_coop_info_purpose_title")}:
                  </strong>
                  <br />
                  {t(activeDocument.descriptionKey)}
                </div>
              </div>
            </div>

            <div className="mt-8 p-6 bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-lg">
              <h3 className="text-xl font-bold mb-2">
                {t("document_consultation_needed")}
              </h3>
              <p className="text-blue-100">{t("document_consultation_desc")}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChinaArbitrationCooperationPage;
